import {
  UseMutateFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { FormSectionDto, UserProfileDto, UserRole } from "@green0meter/api-client";

import { DOMAIN_FORM_SECTIONS_CONFIG } from "../domain";

import { ACTION_GET_FORM_SECTIONS, DOMAIN_FORMS, roleGuard } from "@api";
import { formsSectionsConfigApi } from "@api/services";

export const ACTION_DELETE_FORM_QUESTION_CONFIG = "DELETE_FORM_QUESTION_CONFIG";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

type DeleteFormQuestionConfigDto = {
  questionId: string;
};

const deleteFormQuestionConfig = async (
  questionId: string,
): Promise<DeleteFormQuestionConfigDto> => {
  const response =
    await formsSectionsConfigApi.formSectionsConfigControllerDeleteFormQuestionConfig(
      questionId,
    );

  return response.data;
};

export const useDeleteFormQuestionConfig = (
  user: UserProfileDto | null,
  formId: string,
): {
  deleteFormQuestionConfig: UseMutateFunction<
    DeleteFormQuestionConfigDto,
    Error,
    string,
    unknown
  >;
  deleteFormQuestionConfigEnabled: boolean;
  isSuccessDeleteFormQuestionConfig: boolean;
  isPendingDeleteFormQuestionConfig: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_FORM_SECTIONS_CONFIG, ACTION_DELETE_FORM_QUESTION_CONFIG],
    mutationFn: enabled ? deleteFormQuestionConfig : undefined,
    onSuccess(data) {
      const queryKey = [DOMAIN_FORMS, ACTION_GET_FORM_SECTIONS, formId];
      const currentData = queryClient.getQueryData<FormSectionDto[] | undefined>(
        queryKey,
      );

      queryClient.setQueryData(queryKey, () => {
        return currentData?.map((section) => ({
          ...section,
          formQuestions:
            section.formQuestions?.filter(
              (question) => question.questionId !== data.questionId,
            ) || [],
        }));
      });
    },
  });

  return {
    deleteFormQuestionConfig: mutate,
    deleteFormQuestionConfigEnabled: enabled,
    isSuccessDeleteFormQuestionConfig: isSuccess,
    isPendingDeleteFormQuestionConfig: isPending,
  };
};
